import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slide,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useState, useEffect, useContext } from 'react';
import * as React from 'react';
import { AuthContext } from '../../contexts/auth';
import { getFonts, deleteFont, getFontId, updateFontId, createFont } from '../../services/api';
import { hasPermission, CONTEXTS, SCOPES } from '../../services/permissions';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DOMAIN_LABEL = {
  numeric: 'Apenas números',
  textual: 'Apenas letras',
  alphanumeric: 'Letras e números',
};

export default function Fonts() {
  const { role } = useContext(AuthContext);
  const [fonts, setFonts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFontId, setSelectedFontId] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [currentDeleteId, setCurrentDeleteId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [inputChars, setInputChars] = useState([{ name: '', domain: '', isPrimary: false, errorName: '', errorDomain: '' }]);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [isUpdate, setIsUpdate] = useState(false);
  const [currentId, setCurrentId] = useState('');
  const [emptyName, setEmptyName] = useState(false);
  const [errorNameText, setErrorNameText] = useState('');
  const [emptyDesc, setEmptyDesc] = useState(false);
  const [errorDescText, setErrorDescText] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchFonts = async () => {
    try {
      const { data } = await getFonts();
      setFonts(data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    document.title = 'CEDOMCA | Lista de Fichas Catalográficas';
    fetchFonts().then(() => setLoadingData(false));
  }, []);

  const filteredFonts = fonts.filter((font) => {
    const q = searchQuery.toLowerCase();
    const matchQuery = !q ||
      font.name.toLowerCase().includes(q) ||
      font.description.toLowerCase().includes(q);
    const matchType = !selectedFontId || font.id === selectedFontId;
    return matchQuery && matchType;
  });

  const handleClose = () => {
    setOpen(false);
    setName('');
    setDescription('');
    setInputChars([{ name: '', domain: '', isPrimary: false, errorName: '', errorDomain: '' }]);
    setIsUpdate(false);
    setErrorDescText('');
    setErrorNameText('');
    setEmptyDesc(false);
    setEmptyName(false);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setCurrentDeleteId('');
  };

  const showConfirmDelete = (id) => {
    setCurrentDeleteId(id);
    setOpenConfirm(true);
  };

  const handleDeleteFont = async () => {
    setOpenConfirm(false);
    try {
      await deleteFont(currentDeleteId);
      setCurrentDeleteId('');
      showSnackbar('Ficha catalográfica apagada com sucesso!');
      fetchFonts();
    } catch (err) {
      showSnackbar('Erro ao excluir ficha catalográfica.', 'error');
    }
  };

  const fetchFontId = async (id) => {
    try {
      const { data } = await getFontId(id);
      setName(data.name);
      setDescription(data.description);
      const newInputChar = data.attributes.map((attr) => ({
        name: attr.name,
        domain: attr.domain,
        isPrimary: attr.isPrimary || false,
        errorName: '',
        errorDomain: '',
      }));
      setInputChars(newInputChar);
      setCurrentId(id);
    } catch (err) {
      // ignore
    }
  };

  const handleUpdateFont = (id) => {
    fetchFontId(id);
    setIsUpdate(true);
    setOpen(true);
  };

  const handleCharChanges = (index, event) => {
    let data = [...inputChars];
    data[index][event.target.name] = event.target.value;
    if (event.target.name === 'name') {
      data[index].errorName = '';
    } else {
      data[index].errorDomain = '';
    }
    setInputChars(data);
  };

  const handleCharChangesError = (index, errorField, errorMessage) => {
    let data = [...inputChars];
    if (errorField === 'name') {
      data[index].errorName = errorMessage;
    } else {
      data[index].errorDomain = errorMessage;
    }
    setInputChars(data);
  };

  const isEmptyFields = (data) => {
    let emptyFields = {};
    if (!data.name) {
      setEmptyName(true);
      setErrorNameText('Nome obrigatório');
      emptyFields.name = true;
    }
    if (!data.description) {
      setEmptyDesc(true);
      setErrorDescText('Descrição obrigatória');
      emptyFields.description = true;
    }
    data.attributes.forEach((input, index) => {
      if (!input.name) {
        handleCharChangesError(index, 'name', 'Nome obrigatório');
        emptyFields.attrName = true;
      }
      if (!input.domain) {
        handleCharChangesError(index, 'domain', 'Tipo obrigatório');
        emptyFields.attrDomain = true;
      }
    });
    return Object.keys(emptyFields).length > 0;
  };

  const setNameField = (value) => {
    setErrorNameText('');
    setEmptyName(false);
    setName(value);
  };

  const setDescField = (value) => {
    setErrorDescText('');
    setEmptyDesc(false);
    setDescription(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { name, description, attributes: inputChars };
    if (!isEmptyFields(data)) {
      try {
        await createFont(data);
        handleClose();
        showSnackbar('Ficha catalográfica registrada com sucesso!');
        fetchFonts();
      } catch (err) {
        showSnackbar('Erro ao cadastrar ficha catalográfica.', 'error');
      }
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    const data = { name, description, attributes: inputChars };
    if (!isEmptyFields(data)) {
      try {
        await updateFontId(currentId, data);
        handleClose();
        showSnackbar('Ficha catalográfica editada com sucesso!');
        fetchFonts();
      } catch (err) {
        showSnackbar('Erro ao editar ficha catalográfica.', 'error');
      }
    }
  };

  const handleTogglePrimary = (index) => {
    const data = [...inputChars];
    data[index].isPrimary = !data[index].isPrimary;
    setInputChars(data);
  };

  const handleAddChar = () => {
    setInputChars([...inputChars, { name: '', domain: '', isPrimary: false, errorName: '', errorDomain: '' }]);
  };

  const removeCharField = (index) => {
    setInputChars(inputs => inputs.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '1.5rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Lista de Fichas Catalográficas
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {editMode && (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setOpen(true)}
              sx={{
                backgroundColor: '#7BAAA7',
                '&:hover': { backgroundColor: '#6A9391' },
                color: '#000000',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Cadastrar Ficha
            </Button>
          )}
          {hasPermission(role, CONTEXTS.font, SCOPES.canEdit) && (
            <FormControlLabel
              control={
                <Switch
                  checked={editMode}
                  onChange={() => setEditMode(!editMode)}
                  sx={{
                    '& .MuiSwitch-track': { borderRadius: 12, height: 18 },
                    '& .MuiSwitch-thumb': { width: 14, height: 14 },
                    '& .MuiSwitch-switchBase': { padding: '14px' },
                    '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(16px)' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4a9e8e', opacity: 1 },
                    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': { color: '#fff' },
                  }}
                />
              }
              label="Modo de Edição"
              labelPlacement="start"
            />
          )}
        </Box>
      </Box>

      {!loadingData && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <FilterListIcon />
                <Typography variant="h6">Filtros</Typography>
              </Stack>

              <Typography variant="body2" mb={1}>Buscar</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Nome ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" mb={1}>Tipo de Ficha</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={selectedFontId}
                onChange={(e) => setSelectedFontId(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Todas as fichas</MenuItem>
                {fonts.map((font) => (
                  <MenuItem key={font.id} value={font.id}>{font.name}</MenuItem>
                ))}
              </Select>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => { setSearchQuery(''); setSelectedFontId(''); }}
                sx={{
                  borderColor: '#7BAAA7',
                  color: '#7BAAA7',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#6A9391',
                    color: '#6A9391',
                  }
                }}
              >
                Limpar Filtros
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {filteredFonts.length} ficha{filteredFonts.length !== 1 ? 's' : ''} encontrada{filteredFonts.length !== 1 ? 's' : ''}
            </Typography>

            {filteredFonts.length === 0 ? (
              <Typography variant="h6" sx={{ my: 5, mx: 2 }} color="text.secondary" align="center">
                {fonts.length === 0
                  ? 'Nenhuma ficha catalográfica foi cadastrada ainda :('
                  : 'Nenhuma ficha catalográfica encontrada :('}
              </Typography>
            ) : (
              filteredFonts.map((font) => {
                const isExpanded = expandedId === font.id;
                return (
                  <Box
                    key={font.id}
                    sx={{
                      backgroundColor: '#fff',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      mb: 1.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      onClick={() => setExpandedId(isExpanded ? null : font.id)}
                      sx={{ p: 2, cursor: 'pointer', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.10)' } }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {font.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {font.description}
                        </Typography>
                      </Box>
                      {editMode && (
                        <Stack direction="column" spacing={0.5}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            color="primary"
                            onClick={(e) => { e.stopPropagation(); handleUpdateFont(font.id); }}
                          >
                            Editar
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            color="error"
                            onClick={(e) => { e.stopPropagation(); showConfirmDelete(font.id); }}
                          >
                            Excluir
                          </Button>
                        </Stack>
                      )}
                    </Stack>

                    <Collapse in={isExpanded}>
                      <Divider />
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Grid container sx={{ mb: 0.5 }}>
                          <Grid item xs={5}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#5f7a72', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Nome da Característica
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#5f7a72', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Possíveis Valores
                            </Typography>
                          </Grid>
                          <Grid item xs={2} />
                        </Grid>
                        {font.attributes?.map((attr, i) => (
                          <Grid container key={i} alignItems="center" sx={{ py: 0.5, borderTop: '1px solid #f0f0f0' }}>
                            <Grid item xs={5}>
                              <Typography variant="body2">{attr.name}</Typography>
                            </Grid>
                            <Grid item xs={5}>
                              <Typography variant="body2" color="text.secondary">
                                {DOMAIN_LABEL[attr.domain] || attr.domain}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              {attr.isPrimary && (
                                <Chip
                                  icon={<StarIcon sx={{ fontSize: 14, color: '#f59e0b !important' }} />}
                                  label="Principal"
                                  size="small"
                                  sx={{ fontSize: 11, height: 20, bgcolor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}
                                />
                              )}
                            </Grid>
                          </Grid>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })
            )}
          </Grid>
        </Grid>
      )}

<Dialog
  open={open}
  fullWidth
  maxWidth="md"
  TransitionComponent={Transition}
  keepMounted
  onClose={handleClose}
>
  <DialogTitle>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h6">
        {isUpdate ? 'Editar Ficha Catalográfica' : 'Cadastrar Ficha Catalográfica'}
      </Typography>
      <IconButton onClick={handleClose}>
        <CloseIcon />
      </IconButton>
    </Stack>
  </DialogTitle>
  <DialogContent dividers>
    <Box noValidate component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <TextField
        required
        fullWidth
        label="Nome"
        placeholder="Ex: Fonte institucional"
        value={name}
        error={emptyName}
        helperText={errorNameText}
        onChange={(e) => setNameField(e.target.value)}
      />
      <TextField
        required
        fullWidth
        multiline
        rows={3}
        label="Descrição"
        placeholder="Descreva a ficha catalográfica..."
        value={description}
        error={emptyDesc}
        helperText={errorDescText}
        onChange={(e) => setDescField(e.target.value)}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>Características da Ficha</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddChar}>Adicionar</Button>
      </Stack>

      {inputChars.map((input, index) => (
        <Grid container spacing={2} alignItems="flex-start" key={index}>
          <Grid item xs={4}>
            <TextField
              required
              fullWidth
              label="Nome da característica"
              name="name"
              value={input.name}
              error={input.errorName !== ''}
              helperText={input.errorName || ' '}
              onChange={(event) => handleCharChanges(index, event)}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth error={input.errorDomain !== ''}>
              <InputLabel>Possíveis Valores</InputLabel>
              <Select
                label="Possíveis Valores"
                name="domain"
                value={input.domain}
                onChange={(event) => handleCharChanges(index, event)}
              >
                <MenuItem value="numeric">Apenas números</MenuItem>
                <MenuItem value="textual">Apenas letras</MenuItem>
                <MenuItem value="alphanumeric">Letras e números</MenuItem>
              </Select>
              {input.errorDomain !== '' && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {input.errorDomain}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: '12px !important' }}>
            <Tooltip title={input.isPrimary ? 'Remover como principal' : 'Marcar como principal'} placement="top">
              <IconButton onClick={() => handleTogglePrimary(index)} size="small">
                {input.isPrimary
                  ? <StarIcon sx={{ color: '#f59e0b' }} />
                  : <StarBorderIcon sx={{ color: '#bdbdbd' }} />}
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color={input.isPrimary ? '#92400e' : 'text.disabled'} sx={{ fontSize: 10, lineHeight: 1 }}>
              Principal
            </Typography>
          </Grid>
          <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', pt: '12px !important' }}>
            {inputChars.length > 1 && (
              <IconButton onClick={() => removeCharField(index)}>
                <RemoveCircleIcon color="error" />
              </IconButton>
            )}
          </Grid>
        </Grid>
      ))}
    </Box>
  </DialogContent>
  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button onClick={handleClose} variant="outlined">Cancelar</Button>
    <Button
      variant="contained"
      onClick={isUpdate ? handleSubmitUpdate : handleSubmit}
      sx={{ backgroundColor: '#7BAAA7', '&:hover': { backgroundColor: '#6A9391' }, color: '#000' }}
    >
      {isUpdate ? 'Salvar Alterações' : 'Cadastrar'}
    </Button>
  </DialogActions>
</Dialog>

      <Dialog open={openConfirm} onClose={handleCloseConfirm}>
        <DialogTitle>{'Deseja excluir esta ficha catalográfica?'}</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancelar</Button>
          <Button onClick={handleDeleteFont} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
